import { Head, Link, router } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface PasswordProps {
  currentAgent: Agent
}

export default function Password({ currentAgent }: PasswordProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [processing, setProcessing] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    router.post(
      '/settings/password',
      {
        current_password: currentPassword,
        password: password,
        password_confirmation: passwordConfirmation,
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setProcessing(false)
          setCurrentPassword('')
          setPassword('')
          setPasswordConfirmation('')
        },
      }
    )
  }

  const isFormValid =
    currentPassword.length > 0 && password.length >= 8 && password === passwordConfirmation

  return (
    <>
      <Head title="Alterar Senha" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="password" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">
                <form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-lg border border-gray-200 p-6"
                >
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">Alterar Senha</h2>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="current_password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Senha Atual
                      </label>
                      <div className="relative">
                        <input
                          id="current_password"
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          required
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Nova Senha
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Mínimo de 8 caracteres.</p>
                    </div>

                    <div>
                      <label
                        htmlFor="password_confirmation"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirmar Nova Senha
                      </label>
                      <div className="relative">
                        <input
                          id="password_confirmation"
                          type={showPasswordConfirmation ? 'text' : 'password'}
                          value={passwordConfirmation}
                          onChange={(e) => setPasswordConfirmation(e.target.value)}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          {showPasswordConfirmation ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      {passwordConfirmation && password !== passwordConfirmation && (
                        <p className="mt-1 text-xs text-red-500">As senhas não coincidem.</p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                    <Link
                      href="/settings/profile"
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </Link>
                    <button
                      type="submit"
                      disabled={processing || !isFormValid}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      {processing ? 'Salvando...' : 'Alterar Senha'}
                    </button>
                  </div>
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
